import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AgentsService } from '../../shared/services/agents.service';
import { MarkdownFile } from '../../shared/models/markdown';
import { Agent, AgentVersion } from '../../shared/models/agent';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { playIcon } from "@progress/kendo-svg-icons";
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

export interface Control {
  controlName: string;
  label: string;
  description: string;
  step: number;
  min: number;
  max: number;
  default: number;
}

@Component({
    selector: 'app-agent-configuration',
    templateUrl: './agent-configuration.component.html',
    styleUrls: ['./agent-configuration.component.scss'],
    standalone: false
})
export class AgentConfigurationComponent implements OnInit {
  private readonly ragControls= ['similarityTopK', 'vectorDistanceThreshold'];

  readonly playIcon: SVGIcon = playIcon;

  @Input({required: true}) name: string;
  @Input() containsRagTool = false;

  private service = inject(AgentsService);
  private toastrService = inject(ToastrService);
  private fb = new FormBuilder();
  formGroup: FormGroup;

  gridData: Control[] = [
    {
      controlName: 'temperature',
      label: 'Temperature',
      description: 'Temperature controls how creative or focused the agent\'s responses will be. A lower value (like 0.3) makes the agent more predictable and reliable, which is ideal for delivering accurate information. A higher value (like 0.8 or above) allows for more varied and creative responses, useful when flexibility or brainstorming is needed. The default is 0.7, which offers a balanced approach for most use cases.',
      step: 0.01,
      min: 0.01,
      max: 1,
      default: 0.5,
    },
    {
      controlName: 'topK',
      label: 'Top K',
      description: 'Top K limits the number of possible word choices the agent can consider at each step. Keeping this number low makes responses more predictable, while a higher number introduces more variability. The default value is 40, which is well-optimized for typical scenarios. Unless you\'re working on very specific response patterns, this is best left as-is.',
      step: 1,
      min: 1,
      max: 100,
      default: 40,
    },
    {
      controlName: 'topP',
      label: 'Top P',
      description: 'Top P, also known as nucleus sampling, influences how widely the agent considers different word choices when generating responses. A higher value (e.g., 0.9) gives the agent more freedom to explore diverse outputs, while a lower value keeps it focused and consistent. For most use cases, the default setting of 0.9 works well and rarely needs adjustment unless you\'re fine-tuning tone or style.',
      step: 0.01,
      min: 0.01,
      max: 1,
      default: 0.9,
    },
    {
      controlName: 'similarityTopK',
      label: 'Similarity Top K',
      description: 'This setting determines how many relevant pieces of content the agent pulls from its knowledge base before generating a response. A default value of 5 provides a strong balance—enough context to give rich answers, without slowing things down or introducing noise. Increasing this can help with deeper context, while lowering it may speed things up at the cost of completeness.',
      step: 1,
      min: 1,
      max: 10,
      default: 5,
    },
    {
      controlName: 'vectorDistanceThreshold',
      label: 'Vector Distance Threshold',
      description: 'This controls how closely matched a document must be to be considered relevant. A lower threshold (e.g., 0.3) means only highly relevant information is used, resulting in precise answers. A higher threshold allows looser matches, which might surface broader context but can reduce accuracy. The default value of 0.4 offers a good middle ground, and is typically ideal for most retrieval scenarios.',
      step: 0.0001,
      min: 0.0001,
      max: 1,
      default: 0.5,
    },
  ];

  markdowns: MarkdownFile[] = [];
  currentDeployedAgent: Agent;
  displayedAgent: Agent;
  models: { text: string, value: string} [] = [];
  versions: AgentVersion[] = [];
  statuses: {text: string, value: string}[] = [];

  markdownOptions = {
    hideIcons: [],
  };

  ngOnInit() {
    if (!this.containsRagTool) {
      this.gridData = this.gridData.filter((control) => !this.ragControls.includes(control.controlName));
    }
    this.statuses = [{text: 'Enabled', value: 'enabled'}, {text: 'Disabled', value: 'disabled'}];
    this.loadData();
  }

  private loadData() {
    forkJoin({
      models: this.service.getModels(),
      versions: this.service.getAgentVersions(this.name),
    }).subscribe({
      next: ({models, versions}) => {
        this.models = models.sort().reverse().map(m => ({text: m, value: m}));
        this.versions = versions;

        this.service.getAgent(this.name).subscribe({
          next: (agent) => {
            this.currentDeployedAgent = agent;
            this.updateAgentData(agent);
          },
          error: (err) => console.error(err),
        });
      },
      error: (err) => console.error(err)
    });
  }

  private updateAgentData(agent: Agent) {
    this.displayedAgent = agent;

    if (this.containsRagTool) {
      this.markdowns = agent.markdowns;
    }

    const selectedVersion = this.versions.find(v => v.version === this.displayedAgent.version);
    const selectedModel = this.models.find(m => m.value === this.displayedAgent.model);
    const selectedStatus = this.statuses.find(s => s.value === this.displayedAgent.status);

    if (this.formGroup) {
      const valuesToPatch: { [key: string]: any } = {
        description: this.displayedAgent.description,
        globalInstruction: this.displayedAgent?.globalInstruction,
        instruction: this.displayedAgent.instruction,
        version: selectedVersion,
        model: selectedModel,
        status: selectedStatus,
      }
      this.gridData.forEach((control) => {
        valuesToPatch[control.controlName] = (this.displayedAgent as any)[control.controlName] ?? control.default;
      });
      if (this.containsRagTool) {
        valuesToPatch.chosenMarkdowns = this.displayedAgent.markdowns;
      }
      this.formGroup.patchValue(valuesToPatch);
    } else {
      const gridControlEntries = this.gridData.map(control => {
        const value = (this.displayedAgent as any)[control.controlName] ?? control.default;
        const formState = [
          value,
          [Validators.required, Validators.min(control.min), Validators.max(control.max)]
        ];
        return [control.controlName, formState];
      });

      const gridControlConfig = Object.fromEntries(gridControlEntries);

      const formConfig: { [key: string]: any } = {
        description: [this.displayedAgent.description, Validators.required],
        globalInstruction: [this.displayedAgent?.globalInstruction],
        instruction: [this.displayedAgent.instruction, Validators.required],
        version: [selectedVersion, Validators.required],
        model: [selectedModel, Validators.required],
        status: [selectedStatus, Validators.required],
        ...gridControlConfig
      };

  /*    if (this.containsRagTool) {
        formConfig.chosenMarkdowns = [this.agent.markdowns, Validators.required];
      }*/

      this.formGroup = this.fb.group(formConfig);
    }
  }

  onVersionChange(version: AgentVersion) {
    if (version?.agentVersionId) {
      this.service.getAgentByVersionId(this.name, version.agentVersionId).subscribe({
        next: (res) => {
          this.updateAgentData(res);
          const isCurrentVersion = res.version === this.currentDeployedAgent.version;

          if (isCurrentVersion) {
            this.formGroup.enable();
            this.markdownOptions.hideIcons = [];
          } else {
            this.formGroup.disable();
            this.formGroup.get('version')?.enable();
            this.markdownOptions.hideIcons = ['Bold', 'Italic', 'Heading', 'Reference', 'Link', 'Image', 'Ul', 'Ol', 'Code', 'TogglePreview'];
          }

          setTimeout(() => {
            const aceInputs = document.getElementsByClassName("ace_text-input");
            for (let i = 0; i < aceInputs.length; i++) {
              (aceInputs[i] as HTMLTextAreaElement).disabled = !isCurrentVersion;
            }
          });
        },
        error: (err) => console.error(err)
      });
    }
  }

  deployHandler() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const val = this.formGroup.getRawValue();
    const data = {
      model: val.model.text,
      status: val.status.value,
      description: val.description,
      globalInstruction: val?.globalInstruction,
      instruction: val.instruction,
      temperature: val.temperature,
      topK: val.topK,
      topP: val.topP,
      ...(this.containsRagTool && {
        similarityTopK: val.similarityTopK,
        vectorDistanceThreshold: val.vectorDistanceThreshold,
      }),
      markdownsToDisable: []
    }

    this.service.deployAgent(this.name, data).subscribe({
      next: () => {
        this.toastrService.success('Agent deployed successfully');
        this.loadData();
      },
      error: (err) => this.toastrService.error(err)
    });
  }


}
